// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import { IS_NATIVE } from '../../native/platform.js';

// DECISION: The native-shell UI (bottom tab bar, card register, quick entry,
// native pickers) is gated on Capacitor.isNativePlatform() — NOT viewport width.
// A desktop browser resized narrow keeps the desktop layout; only the packaged
// app opts in. This matches the spec's "single source of truth" requirement.
//
// The one exception is a DEBUG-ONLY preview switch: because this build machine
// has no Android emulator (no KVM), the only way to visually verify the mobile
// UI is to force it on in a normal browser. Appending `?mobile=1` (persisted to
// localStorage) or setting localStorage['hushold_force_mobile']='1' flips the shell
// on for previews/screenshots. It defaults OFF and never affects the shipped app,
// where IS_NATIVE already decides.
const FORCE_KEY = 'hushold_force_mobile';

function readForceFlag(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const params = new URLSearchParams(window.location.search);
    const q = params.get('mobile');
    if (q === '1') localStorage.setItem(FORCE_KEY, '1');
    if (q === '0') localStorage.removeItem(FORCE_KEY);
    return localStorage.getItem(FORCE_KEY) === '1';
  } catch {
    return false;
  }
}

export function isMobileShell(): boolean {
  return IS_NATIVE || readForceFlag();
}

// Hook form for components. The value is stable for the app's lifetime (platform
// and the debug flag don't change mid-session), so a plain read is sufficient.
export function useMobileShell(): boolean {
  return isMobileShell();
}
